import React, {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import { BUTTON_VARIANT } from 'constants/interfaces';

import type IValueChainMapAnalysisItem from 'store/slices/valueChainMap/interfaces/IValueChainMapAnalysisItem';
import type IImpactScenariosList from './interfaces/IImpactScenariosList';

import styles from './sass/ImpactScenariosList.module.scss';

function ImpactScenariosList({
  scenarios,
  onOpenAnalysis,
  onDeleteAnalysis,
}: IImpactScenariosList): React.JSX.Element {
  const { t } = useTranslation();
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [menuPlacement, setMenuPlacement] = useState<'down' | 'up'>('down');
  const menuRef = useRef<HTMLDivElement>(null);
  const scenariosListRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (openMenuId === null) {
      setMenuPlacement('down');
      return;
    }

    const listElement = scenariosListRef.current;
    const triggerElement = document.querySelector<HTMLElement>(
      `[data-impact-scenario-menu-trigger="${openMenuId}"]`,
    );

    if (!listElement || !triggerElement) {
      return;
    }

    const menuElement = dropdownMenuRef.current;
    const gap = 4;
    const fallbackMenuHeight = 72;
    const menuHeight = Math.max(menuElement?.offsetHeight ?? 0, fallbackMenuHeight);
    const triggerRect = triggerElement.getBoundingClientRect();
    const listRect = listElement.getBoundingClientRect();
    const spaceBelow = listRect.bottom - triggerRect.bottom - gap;
    const spaceAbove = triggerRect.top - listRect.top - gap;

    if (spaceBelow >= menuHeight) {
      setMenuPlacement('down');
      return;
    }

    if (spaceAbove >= menuHeight) {
      setMenuPlacement('up');
      return;
    }

    setMenuPlacement(spaceAbove > spaceBelow ? 'up' : 'down');
  }, [openMenuId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  const handleToggleMenu = (id: number) => {
    setOpenMenuId((prevOpenMenuId) => (prevOpenMenuId === id ? null : id));
  };

  const handleOpenScenario = (scenario: IValueChainMapAnalysisItem) => {
    onOpenAnalysis(scenario);
  };

  const handleEditNameClick = (scenario: IValueChainMapAnalysisItem) => {
    onOpenAnalysis(scenario);
    setOpenMenuId(null);
  };

  const handleDeleteClick = (scenario: IValueChainMapAnalysisItem) => {
    onDeleteAnalysis(scenario.valueChainMapId, scenario.id);
    setOpenMenuId(null);
  };

  if (scenarios.length === 0) {
    return (
      <div className={styles.noResults}>
        {t('valueChainMap.impactScenariosNoResults')}
      </div>
    );
  }

  return (
    <div ref={scenariosListRef} className={styles.scenariosList}>
      {scenarios.map((scenario) => (
        <div
          key={scenario.id}
          className={clsx(styles.scenarioItem, {
            [styles.scenarioItemActive]: openMenuId === scenario.id,
          })}
        >
          <Button
            title={scenario.name}
            variant={BUTTON_VARIANT.ghost}
            className={styles.scenarioItemButton}
            onClick={() => handleOpenScenario(scenario)}
          />
          <div
            className={styles.scenarioActionsWrapper}
            ref={openMenuId === scenario.id ? menuRef : null}
            data-impact-scenario-menu-trigger={scenario.id}
          >
            <Button
              title={t('common.actions')}
              variant={BUTTON_VARIANT.ghost}
              className={styles.scenarioActions}
              startIcon={(
                <span className={styles.scenarioActionsIcon} aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              )}
              onClick={() => handleToggleMenu(scenario.id)}
            />
            {openMenuId === scenario.id ? (
              <div
                ref={dropdownMenuRef}
                className={clsx(styles.dropdownMenu, {
                  [styles.dropdownMenuUp]: menuPlacement === 'up',
                })}
              >
                <Button
                  title={t('common.edit')}
                  variant={BUTTON_VARIANT.ghost}
                  className={styles.dropdownItem}
                  onClick={() => handleEditNameClick(scenario)}
                />
                <Button
                  title={t('common.delete')}
                  variant={BUTTON_VARIANT.ghost}
                  className={styles.dropdownItem}
                  onClick={() => handleDeleteClick(scenario)}
                />
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

export default ImpactScenariosList;
